import { useCallback, useEffect } from "react";

type KeyHandler = (e: KeyboardEvent) => void;

interface ShortcutMap {
	[key: string]: KeyHandler;
}

/**
 * Register global keyboard shortcuts.
 * Keys follow format: "mod+k" (mod = Cmd on Mac, Ctrl elsewhere),
 * "shift+enter", "1", "escape", etc.
 */
export function useKeyboardShortcuts(shortcuts: ShortcutMap, enabled = true) {
	const handleKeyDown = useCallback(
		(e: KeyboardEvent) => {
			if (!enabled) return;

			const target = e.target as HTMLElement;
			const isInput =
				target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable;

			const parts: string[] = [];
			if (e.metaKey || e.ctrlKey) parts.push("mod");
			if (e.shiftKey) parts.push("shift");
			if (e.altKey) parts.push("alt");
			parts.push(e.key.toLowerCase());

			const combo = parts.join("+");

			const handler = shortcuts[combo];
			if (handler) {
				// Allow mod+k even in inputs (command palette)
				if (isInput && combo !== "mod+k" && combo !== "escape") return;
				e.preventDefault();
				e.stopPropagation();
				handler(e);
			}
		},
		[shortcuts, enabled],
	);

	useEffect(() => {
		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [handleKeyDown]);
}
